/**
 * timeline/timelineUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Pure utility functions — no React, no side effects.
 * All date arithmetic lives here so components stay declarative.
 */

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { BASE_URL } from '@/config/serverApiConfig';

// Extend once here — timelineUtils is the single entry point for all date
// arithmetic, so plugins are guaranteed to be loaded before any call site.
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

// ── Grid column generation ────────────────────────────────────────────────────

/**
 * Generates the ordered list of column headers for the Gantt grid.
 *
 * @param {'day'|'week'|'month'|'quarter'} mode
 * @param {dayjs.Dayjs} anchorDate  - the "today" anchor (start of the visible window)
 * @returns {{ key: string, label: string, isToday: boolean, start: dayjs.Dayjs, end: dayjs.Dayjs }[]}
 */
export function buildColumns(mode, anchorDate) {
    const today = dayjs(); // browser always returns local time
    const cols = [];

    if (mode === 'day') {
        // Show 21 days centred around anchor (10 before, 10 after + today)
        const start = anchorDate.subtract(10, 'day');
        for (let i = 0; i < 21; i++) {
            const d = start.add(i, 'day');
            cols.push({
                key: d.format('YYYY-MM-DD'),
                label: d.format('ddd D'),
                shortLabel: d.format('D'),
                isToday: d.isSame(today, 'day'),
                isWeekend: d.day() === 0 || d.day() === 6,
                start: d.startOf('day'),
                end: d.endOf('day'),
            });
        }
    } else if (mode === 'week') {
        // Show 12 weeks starting from the week containing anchor
        const start = anchorDate.startOf('week');
        for (let i = 0; i < 12; i++) {
            const wStart = start.add(i, 'week');
            const wEnd = wStart.endOf('week');
            cols.push({
                key: `${wStart.format('YYYY')}-W${wStart.isoWeek()}`,
                label: `${wStart.format('D MMM')}`,
                shortLabel: `W${wStart.isoWeek()}`,
                isToday: today.isBetween(wStart, wEnd, 'day', '[]'),
                isWeekend: false,
                start: wStart,
                end: wEnd,
            });
        }
    } else if (mode === 'month') {
        // Show 9 months starting from anchor's month
        const start = anchorDate.startOf('month');
        for (let i = 0; i < 9; i++) {
            const mStart = start.add(i, 'month');
            const mEnd = mStart.endOf('month');
            cols.push({
                key: mStart.format('YYYY-MM'),
                label: mStart.format('MMM YYYY'),
                shortLabel: mStart.format('MMM'),
                isToday: today.isBetween(mStart, mEnd, 'day', '[]'),
                isWeekend: false,
                start: mStart,
                end: mEnd,
            });
        }
    } else if (mode === 'quarter') {
        // Show 6 quarters starting from anchor's quarter
        const qStart = anchorDate.startOf('quarter');
        for (let i = 0; i < 6; i++) {
            const start = qStart.add(i * 3, 'month');
            const end = start.add(2, 'month').endOf('month');
            cols.push({
                key: start.format('YYYY-[Q]Q'),
                label: `Q${start.quarter()} ${start.year()}`,
                shortLabel: `Q${start.quarter()}`,
                isToday: today.isBetween(start, end, 'day', '[]'),
                isWeekend: false,
                start,
                end,
            });
        }
    }

    return cols;
}

// ── Bar positioning ───────────────────────────────────────────────────────────

/**
 * Computes the left offset (as a fraction 0–1) and width (as a fraction of
 * total grid width) for a task bar given the grid's visible date range.
 *
 * Returns null when the task has no usable dates or falls entirely outside
 * the visible window.
 *
 * @param {object} task
 * @param {dayjs.Dayjs} gridStart  - start of the leftmost column
 * @param {dayjs.Dayjs} gridEnd    - end of the rightmost column
 * @returns {{ leftFraction: number, widthFraction: number } | null}
 */
export function computeBarPosition(task, gridStart, gridEnd) {
    const end = task.dueDate ? dayjs(task.dueDate).endOf('day') : null;
    // Fallback to createdAt, then dueDate - 3 days if startDate is missing
    const start = task.startDate
        ? dayjs(task.startDate).startOf('day')
        : (task.createdAt ? dayjs(task.createdAt).startOf('day') : (end ? end.subtract(3, 'day').startOf('day') : null));

    if (!start && !end) return null;

    const effectiveStart = start;
    const effectiveEnd = end || start.add(1, 'day');

    // Entirely outside the visible window — render nothing
    if (effectiveEnd.valueOf() <= gridStart.valueOf()) return null;
    if (effectiveStart.valueOf() >= gridEnd.valueOf()) return null;

    const totalMs = gridEnd.valueOf() - gridStart.valueOf();
    if (totalMs <= 0) return null;

    // Clip to the visible window
    const visibleStart = Math.max(effectiveStart.valueOf(), gridStart.valueOf());
    const visibleEnd = Math.min(effectiveEnd.valueOf(), gridEnd.valueOf());

    const leftFraction = (visibleStart - gridStart.valueOf()) / totalMs;
    const widthFraction = (visibleEnd - visibleStart) / totalMs;

    return { leftFraction, widthFraction };
}

/**
 * Computes the pixel offset for the "today" indicator line
 * within the full grid width.
 *
 * @param {dayjs.Dayjs} gridStart
 * @param {dayjs.Dayjs} gridEnd
 * @param {number}      gridWidthPx
 * @returns {number|null} px from left, or null if today is outside the window
 */
export function computeTodayOffset(gridStart, gridEnd, gridWidthPx) {
    const today = dayjs(); // browser always returns local time
    if (!today.isBetween(gridStart, gridEnd, 'minute', '[]')) return null;
    const totalMs = gridEnd.valueOf() - gridStart.valueOf();
    return ((today.valueOf() - gridStart.valueOf()) / totalMs) * gridWidthPx;
}

// ── Task grouping ─────────────────────────────────────────────────────────────

/**
 * Groups a flat task array into a Map<statusKey, task[]>.
 *
 * @param {object[]} tasks
 * @param {string[]} sectionKeys
 * @returns {Map<string, object[]>}
 */
export function groupTasksByStatus(tasks, sectionKeys) {
    const map = new Map(sectionKeys.map((k) => [k, []]));
    tasks.forEach((task) => {
        const bucket = map.get(task.status);
        if (bucket) bucket.push(task);
    });
    return map;
}

// ── Time helpers (reused from kanban for consistency) ─────────────────────────

export function parseEstimatedToMs(str) {
    if (!str) return 0;
    let ms = 0;
    const h = str.match(/(\d+(?:\.\d+)?)h/);
    const m = str.match(/(\d+)m/);
    if (h) ms += parseFloat(h[1]) * 3_600_000;
    if (m) ms += parseInt(m[1]) * 60_000;
    return ms;
}

export function formatMs(ms) {
    if (!ms || ms <= 0) return '0h';
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Name / avatar helpers ─────────────────────────────────────────────────────

export function getInitials(name = '') {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
}

export function getFullName(person) {
    if (!person) return '';
    return [person.name, person.surname].filter(Boolean).join(' ').trim() || person.name || '';
}

export function getPhotoUrl(photo) {
    if (!photo) return null;
    if (/^https?:\/\//i.test(photo)) return photo;
    const relative = photo.startsWith('/') ? photo : `/public/uploads/admin/${photo}`;
    const base = (BASE_URL || '').replace(/\/$/, '');
    return `${base}${relative}`;
}

// ── Status helpers ────────────────────────────────────────────────────────────

export function isOverdue(task) {
    if (!task.dueDate) return false;
    if (['Completed', 'Cancelled'].includes(task.status)) return false;
    return dayjs(task.dueDate).isBefore(dayjs(), 'day');
}

export function getCompletionPct(task) {
    const est = parseEstimatedToMs(task.estimatedTime);
    if (!est || !task.actualTimeMs) return 0;
    return Math.min(Math.round((task.actualTimeMs / est) * 100), 100);
}

// ── Metrics computation ───────────────────────────────────────────────────────

/**
 * Derives the 7 headline metrics shown above the timeline.
 *
 * @param {object[]} tasks
 * @returns {{ total, active, upcoming, overdue, underReview, completed, onHold }}
 */
export function computeMetrics(tasks) {
    const today = dayjs();
    let active = 0, upcoming = 0, overdue = 0, underReview = 0, completed = 0, onHold = 0;

    tasks.forEach((t) => {
        if (t.status === 'In Progress') active++;
        if (t.status === 'Under Review') underReview++;
        if (t.status === 'Completed') completed++;
        if (t.status === 'On Hold') onHold++;
        if (['Backlog', 'In Progress'].includes(t.status) && t.startDate && dayjs(t.startDate).isAfter(today, 'day')) {
            upcoming++;
        }
        if (isOverdue(t)) overdue++;
    });

    return { total: tasks.length, active, upcoming, overdue, underReview, completed, onHold };
}

// ── AI Insights generation ────────────────────────────────────────────────────

/**
 * Derives AI-style insights from the task list without an external API call.
 * Returns an array of { type: 'error'|'warning'|'info', title, message }.
 */
export function deriveInsights(tasks) {
    const insights = [];
    const today = dayjs();

    // Overdue tasks
    const overdueTasks = tasks.filter(isOverdue);
    if (overdueTasks.length > 0) {
        insights.push({
            type: 'error',
            icon: 'warning',
            title: `${overdueTasks.length} Overdue Task${overdueTasks.length > 1 ? 's' : ''}`,
            message: overdueTasks.slice(0, 2).map((t) => t.title).join(', ') +
                (overdueTasks.length > 2 ? ` +${overdueTasks.length - 2} more` : ''),
            tasks: overdueTasks,
        });
    }

    // Tasks in review > 48h (approximated by updatedAt)
    const staleReview = tasks.filter(
        (t) => t.status === 'Under Review' && t.updatedAt && today.diff(dayjs(t.updatedAt), 'hour') > 48
    );
    if (staleReview.length > 0) {
        insights.push({
            type: 'warning',
            icon: 'schedule',
            title: 'Review Bottleneck',
            message: `${staleReview.length} task${staleReview.length > 1 ? 's' : ''} pending review for >48h`,
            tasks: staleReview,
        });
    }

    // Assignee overload — anyone with > 4 active tasks
    const assigneeCounts = {};
    tasks.filter((t) => t.status === 'In Progress').forEach((t) => {
        const name = getFullName(t.assignee);
        if (name) assigneeCounts[name] = (assigneeCounts[name] || 0) + 1;
    });
    Object.entries(assigneeCounts).forEach(([name, count]) => {
        if (count > 4) {
            const overloadedTasks = tasks.filter(
                (t) => t.status === 'In Progress' && getFullName(t.assignee) === name
            );
            insights.push({
                type: 'info',
                icon: 'balance',
                title: 'Workload Alert',
                message: `${name} has ${count} active tasks — consider redistribution`,
                tasks: overloadedTasks,
            });
        }
    });

    // Upcoming deadlines in 3 days
    const soonDue = tasks.filter(
        (t) =>
            !['Completed', 'Cancelled'].includes(t.status) &&
            t.dueDate &&
            dayjs(t.dueDate).isBetween(today, today.add(3, 'day'), 'day', '(]')
    );
    if (soonDue.length > 0) {
        insights.push({
            type: 'info',
            icon: 'event_upcoming',
            title: 'Upcoming Deadlines',
            message: `${soonDue.length} task${soonDue.length > 1 ? 's' : ''} due in the next 3 days`,
            tasks: soonDue,
        });
    }

    // Scheduling conflicts — dependent task starts before blocker finishes
    const conflictedTasks = tasks.filter(t => {
        if (!t.dependencies?.length || !t.startDate) return false;
        return t.dependencies.some(dep => {
            const blocker = tasks.find(b => String(b._id) === String(dep.taskId?._id || dep.taskId));
            return blocker?.dueDate && new Date(t.startDate) < new Date(blocker.dueDate);
        });
    });
    if (conflictedTasks.length > 0) {
        insights.push({
            type: 'error',
            icon: 'warning',
            title: 'Scheduling Conflict',
            message: `${conflictedTasks.length} task${conflictedTasks.length > 1 ? 's' : ''} start before their blocker finishes`,
            tasks: conflictedTasks,
        });
    }

    return insights;
}

// ── Dependency line computation ───────────────────────────────────────────────

/**
 * Builds a map of taskId → { leftPx, rightPx, midY } for all visible tasks.
 * Used to compute SVG connector line coordinates.
 *
 * @param {object[]} tasks
 * @param {dayjs.Dayjs} gridStart
 * @param {dayjs.Dayjs} gridEnd
 * @param {number} gridWidthPx
 * @param {number} ROW_HEIGHT
 * @param {number} SECTION_ROW_HEIGHT
 * @param {string[]} sectionKeys  — ordered section keys (determines row Y offset)
 * @param {Map<string, object[]>} grouped — task arrays per section key
 * @returns {Map<string, { leftPx: number, rightPx: number, midY: number }>}
 */
export function buildBarPositionMap(tasks, gridStart, gridEnd, gridWidthPx, ROW_HEIGHT, SECTION_ROW_HEIGHT, sectionKeys, grouped, isSectionCollapsed) {
    const map = new Map();
    let yOffset = 0;

    for (const key of sectionKeys) {
        yOffset += SECTION_ROW_HEIGHT; // section header row
        
        if (isSectionCollapsed && isSectionCollapsed(key)) {
            continue; // Skip tasks in collapsed sections so lines aren't drawn and yOffset isn't incremented
        }
        
        const sectionTasks = grouped.get(key) || [];
        for (const task of sectionTasks) {
            const pos = computeBarPosition(task, gridStart, gridEnd);
            if (pos) {
                const leftPx = pos.leftFraction * gridWidthPx;
                const rightPx = leftPx + Math.max(pos.widthFraction * gridWidthPx, 160);
                const midY = yOffset + ROW_HEIGHT / 2;
                map.set(String(task._id), { leftPx, rightPx, midY });
            }
            yOffset += ROW_HEIGHT;
        }
    }

    return map;
}

/**
 * Computes SVG path data for finish-to-start dependency lines.
 * Returns an array of line descriptors ready to render as <path> elements.
 *
 * @param {object[]} tasks  — flat task array
 * @param {Map} barPositionMap  — from buildBarPositionMap
 * @returns {{ d: string, isAtRisk: boolean, fromTaskId: string, toTaskId: string }[]}
 */
export function computeDependencyLines(tasks, barPositionMap) {
    const lines = [];

    for (const task of tasks) {
        if (!task.dependencies?.length) continue;

        const toPos = barPositionMap.get(String(task._id));
        if (!toPos) continue;

        for (const dep of task.dependencies) {
            const blockerId = String(dep.taskId?._id || dep.taskId);
            const fromPos = barPositionMap.get(blockerId);
            if (!fromPos) continue;

            const blockerTask = tasks.find(t => String(t._id) === blockerId);
            const type = dep.type || 'finish_to_start';

            let x1, y1, x2, y2, d;

            if (type === 'finish_to_start') {
                // Blocker right edge → this task left edge
                x1 = fromPos.rightPx;
                y1 = fromPos.midY;
                x2 = toPos.leftPx;
                y2 = toPos.midY;
                const midX = x1 + (x2 - x1) / 2;
                d = `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`;
            } else if (type === 'start_to_start') {
                // Blocker left edge → this task left edge
                x1 = fromPos.leftPx;
                y1 = fromPos.midY;
                x2 = toPos.leftPx;
                y2 = toPos.midY;
                const offsetX = Math.min(x1, x2) - 16; // elbow goes left of both bars
                d = `M ${x1} ${y1} H ${offsetX} V ${y2} H ${x2}`;
            } else if (type === 'finish_to_finish') {
                // Blocker right edge → this task right edge
                x1 = fromPos.rightPx;
                y1 = fromPos.midY;
                x2 = toPos.rightPx;
                y2 = toPos.midY;
                const offsetX = Math.max(x1, x2) + 16; // elbow goes right of both bars
                d = `M ${x1} ${y1} H ${offsetX} V ${y2} H ${x2}`;
            } else {
                continue;
            }

            const isAtRisk = blockerTask?.dueDate && task.startDate
                ? new Date(task.startDate) < new Date(blockerTask.dueDate)
                : false;

            const DEP_LABEL = {
                finish_to_start: 'Finish → Start',
                start_to_start: 'Start → Start',
                finish_to_finish: 'Finish → Finish',
            };

            lines.push({
                d,
                isAtRisk,
                type,
                fromTaskId: blockerId,
                toTaskId: String(task._id),
                fromTitle: blockerTask?.title || dep.taskId?.title || 'Unknown task',
                toTitle: task.title,
                fromDue: blockerTask?.dueDate || null,
                toStart: task.startDate || null,
                typeLabel: DEP_LABEL[type] || type,
            });
        }
    }

    return lines;
}
